import {
  Card,
  Stack,
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
} from "@mui/material";
import { FEATURE } from "src/util/enums";
import { useAccess } from "src/hooks/useAccess.hook";
import Page from "../Page";
import { BaseTable } from "../BaseTable/BaseTable";
import { chargerPointColumns, licenseColumns, tabsToShow } from "./charger.constants";
import { useDispatch, useSelector } from "react-redux";
import { dialogSlice } from "src/store/dailog/dailog.slice";
import { RemoteOperationModal } from "./RemoteOperation/RemoteOperationModal";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo } from "react";
import { remoteOpsStatusSlice } from "src/store/remoteOpsStatus/remoteOpsStatus.slice";
import { tabsCurrentlyOpenedForSlice } from "src/store/tabsOpened/tabsOpened.slice";
import { CommonSearchbar } from "src/layouts/dashboard/Searchbar";
import ExportCSV from "../ExportCsv/ExportCSV";
import { useSelectedTeam } from "src/hooks/useSelectedTeam";
import { ChargerLimitModal } from "./ChargerLimitModal";
import { useFetchOnDemand, useList } from "src/hooks/useFetch.hook";
import { useQuery } from "@tanstack/react-query";
import { fetchWithError } from "src/react-query/fetchWithError";
import { queryKeys } from "src/react-query/queryKeys";
import { selector } from "src/react-query/select";
import { is } from "src/util/is";
import { debounceFunc } from "src/utils/helper";
import { Icon } from "@iconify/react";
import AlertCircleFill from "@iconify/icons-eva/alert-circle-fill";
import alertTriangleFill from "@iconify/icons-eva/alert-triangle-fill";
import closeFill from "@iconify/icons-eva/close-fill";
import clockOutline from "@iconify/icons-eva/clock-outline";
import { TabContext, TabPanel } from "@mui/lab";
import { RootTabItemStyles, RootTabStyle } from "src/theme/overrides/Tabs";

export default function ChargerList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { teamId } = useSelectedTeam();
  const hasCreateAccess = useAccess(FEATURE.CHARGER, "C");
  const [pageSize, setPageSize] = useState(10);
  const [searchValue, setSearchValue] = useState("");
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const isReadOnly = useAccess(FEATURE.CHARGER, "R");
  const isViewOnly = useAccess(FEATURE.CHARGER, "V");
  const {
    data: { chargers: chargers = [], totalRecords = 0 } = {},
    isLoading, mutate: fetchData
  } = useFetchOnDemand("chargerList", false, false);
  
  const { data: { allowed_licenses: allowedChargerCount } = {}, isFetching: isCountLoading } = useQuery(
    queryKeys.allowedChargerCount(teamId),
    fetchWithError,
    {
      enabled: hasCreateAccess && !is.empty(teamId),
      select: selector,
    }
  );
  
  const { open, data } = useSelector(({ dialog }) => dialog);
  const [sortModel, setSortModel] = useState([
    { field: "status", sort: "asc" },
  ]);
  const [tableData, setTableData] = useState();
  const [licenseModalOpen, setLicenseModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState("expiredLicenses");
  
  const { data: licenseDetailsData, isFetching: licenseDetailsFetching } = useList(
    "licensesDetails",
    true,
    {}
  );
  const expiredLicenses = licenseDetailsData?.chargersWithoutLicenseCount || 0;
  const upcomingExpiringLicenses = licenseDetailsData?.chargersWithUpcomingLicenseExpiryCount || 0;
  const chargersWithoutLicense = licenseDetailsData?.chargersWithoutLicense || [];
  const chargersWithUpcomingLicenseExpiry = licenseDetailsData?.chargersWithUpcomingLicenseExpiry || [];

  const handleClose = () => {
    dispatch(dialogSlice.actions.setDialog(false));
  };

  // License Modal Handlers
  const handleLicenseModalOpen = () => {
    setLicenseModalOpen(true);
  };

  const handleLicenseModalClose = () => {
    setLicenseModalOpen(false);
  };

  const handleViewChargepoint = (charge_box_pk) => {
    navigate(`/charger/${charge_box_pk}`);
    handleLicenseModalClose();
  };

  const handleTabChange = (e, tab) => {
    setSelectedTab(tab);
  };

  useEffect(() => {
    let data = [...chargers];
    setTableData(data);
  }, [chargers]);

  const handleRemoteOpsStatusModalClose = () => {
    dispatch(
      remoteOpsStatusSlice.actions.setRemoteOps({
        open: false,
        remoteOperationPerformed: null,
        operationDetails: { chargerId: null, response: null },
      })
    );
  };
  const rowOrCellClickRedirect = ({ id }) => {
    // if (isReadOnly) return;
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: id,
        tabOpenedFor: "charger",
        tabOpened: "general",
      })
    );
    navigate(`/charger/${id}`);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setPaginationModel({ page: 0, pageSize: newPageSize });
  };

  const handlePageChange = (newPage) => {
    setPaginationModel(prev => ({ page: newPage, pageSize: prev.pageSize }));
  };

  useEffect(() => {
    fetchData({ page: paginationModel.page, pageSize: paginationModel.pageSize, search: searchValue });
  }, [paginationModel.page, paginationModel.pageSize, searchValue]);

  // Memoize charger limit logic
  const chargerLimitStatus = useMemo(() => {
    // If user doesn't have create access, we don't need limit checking
    if (!hasCreateAccess) {
      return {
        isLoading: false,
        canCreate: false,
        isLimitReached: false,
        message: 'No create access'
      };
    }

    if (isCountLoading || allowedChargerCount === undefined) {
      return {
        isLoading: true,
        canCreate: false,
        message: 'Loading charger limits...'
      };
    }

    const isLimitReached = totalRecords >= allowedChargerCount;
    return {
      isLoading: false,
      canCreate: !isLimitReached,
      isLimitReached,
      message: isLimitReached
        ? `Charger limit reached (${totalRecords}/${allowedChargerCount})`
        : `${totalRecords}/${allowedChargerCount} chargers used`,
      allowedCount: allowedChargerCount,
      currentCount: totalRecords
    };
  }, [hasCreateAccess, isCountLoading, allowedChargerCount, totalRecords]);

  const handleCreateCharger = () => {
    dispatch(
      tabsCurrentlyOpenedForSlice.actions.setTabsOpened({
        detailsId: "",
        tabOpenedFor: "charger",
        tabOpened: "general",
      })
    );
    navigate("new");
  };

  const getActions = () => {
    if (!hasCreateAccess) return [];

    // Don't show button if limit is reached or still loading
    if (chargerLimitStatus?.isLimitReached || chargerLimitStatus?.isLoading) {
      return [];
    }

    return [{
      text: "Add Charge Point",
      action: handleCreateCharger,
    }];
  };

  return (
    <Page
      title="Charge Points | Smart-CMS"
      actions={getActions()}
      sx={{ p: 2, mt: -4 }}
    >
      {/* Charger Limit Reached Banner */}
      {hasCreateAccess && chargerLimitStatus?.isLimitReached && (
        <Box
          sx={{
            mb: 2,
            p: 2,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #ffeaa7 0%, #fdcb6e 50%, #f39c12 100%)',
            color: '#2d3436',
            position: 'relative',
            overflow: 'hidden',
            boxShadow: '0 4px 16px rgba(243, 156, 18, 0.25)',
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Box
                sx={{
                  mr: 1.5,
                  p: 1,
                  borderRadius: 1.5,
                  backgroundColor: 'rgba(45, 52, 54, 0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Icon
                  icon={AlertCircleFill}
                  width={24}
                  height={24}
                  style={{ color: '#2d3436' }}
                />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    textShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    fontSize: '1.1rem',
                    mb: 0.5
                  }}
                >
                  {chargerLimitStatus?.allowedCount === 0 ? 'No Chargers Allowed!' : 'Charger Limit Reached!'}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    opacity: 0.9,
                    lineHeight: 1.4,
                    fontSize: '0.95rem'
                  }}
                >
                  {chargerLimitStatus?.allowedCount === 0
                    ? 'Your current plan does not allow any chargers. Please contact support to upgrade your plan and enable charger creation.'
                    : `You have reached the maximum allowed chargers (${chargerLimitStatus?.currentCount}/${chargerLimitStatus?.allowedCount}). Please contact support to increase your limit.`
                  }
                  <br />
                  Please contact support team at <a href="mailto:zynkasupport@zynka.ai">zynkasupport@zynka.ai</a> or  +919429693773 to buy/get additional licences.
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}
      {/* License Status Banner */}
      {isViewOnly && (expiredLicenses > 0 || upcomingExpiringLicenses > 0) && (
        <Box sx={{ mt: 2, mb: 3 }}>
          <Box
            sx={(theme) => ({
              bgcolor: theme.palette.mode === 'dark' ? theme.palette.background.paper : '#ffffff',
              border: `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : '#E0E0E0'}`,
              borderRadius: '16px',
              p: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: theme.palette.mode === 'dark'
                ? '0px 2px 12px rgba(37, 99, 235, 0.12)'
                : '0px 2px 4px rgba(0,0,0,0.1)',
              width: '100%',
            })}
          >
            {/* LEFT SIDE (All text + icons) */}
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
              <Box>
                {/* Title */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                  <Icon icon="eva:file-text-outline" width={24} height={24} />
                  <Typography variant="h6" sx={(theme) => ({ fontWeight: 600, color: theme.palette.mode === 'dark' ? theme.palette.primary.main : '#333' })}>
                    License Status
                  </Typography>
                </Box>

                {/* Messages */}
                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                  {expiredLicenses > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Icon icon={alertTriangleFill} color={theme => theme.palette.warning.main} width={22} />
                      <Typography variant="body2" sx={theme => ({ color: theme.palette.error.main })}>
                        {expiredLicenses} License need immediate attention
                      </Typography>
                    </Box>
                  )}

                  {upcomingExpiringLicenses > 0 && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Icon icon={clockOutline} color={theme => theme.palette.warning.main} width={22} />
                      <Typography variant="body2" sx={theme => ({ color: theme.palette.warning.main })}>
                        {upcomingExpiringLicenses} upcoming License expiry
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>

            {/* RIGHT SIDE BUTTON */}
            {expiredLicenses > 0 && (
              <Button
                variant="outlined"
                size="small"
                onClick={handleLicenseModalOpen}
                sx={{
                  textTransform: "none",
                  fontWeight: 500,
                  minWidth: 90,
                }}
              >
                View Details
              </Button>
            )}
          </Box>
        </Box>
      )}

      <Card sx={{ p: 2, mt: 2 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <CommonSearchbar
            placeholder="Search"
            searchFunc={(e) =>
              debounceFunc(() => {
                setPaginationModel({ page: 0, pageSize: 10 });
                setSearchValue(e.target.value);
              }, 800)()}
          />
          <ExportCSV
            rows={tableData || []}
            columns={chargerPointColumns(isReadOnly)}
            csvMorphemes="chargers"
            csvColumns={chargerPointColumns(isReadOnly)}
          />
        </Stack>
        <BaseTable
          rows={tableData ?? []}
          columns={chargerPointColumns(isReadOnly)}
          loading={isLoading}
          getRowId={(row) => row.chargepoint_pk}
          rowOrCellClickRedirect={rowOrCellClickRedirect}
          pageSize={pageSize}
          sortModel={sortModel}
          onSortModelChange={(model) => setSortModel(model)}
          isReadOnly={isReadOnly}
          showExportCsvBtn={false}
          csvMorphemes="chargers"
          csvColumns={chargerPointColumns(isReadOnly)}
          rowHeight={80}
          paginationModel={paginationModel}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          totalRows={totalRecords || 0}
          serverRowFetcher={true}
        />
      </Card>
      {open && data?.modalType === "remoteoperation" && (
        <RemoteOperationModal
          open={open}
          chargerId={data?.id}
          chargerPk={data?.pk}
          onClose={handleClose}
        />
      )}
      {open && data === "ChargerLimitModal" && (
        <ChargerLimitModal open={open} onClose={handleClose} />
      )}

      {/* License Details Modal */}
      <Dialog
        open={licenseModalOpen}
        onClose={handleLicenseModalClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "end",
          }}
        >
          <IconButton onClick={handleLicenseModalClose}>
            <Icon icon={closeFill} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 0 }}>
          {licenseDetailsFetching ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: 200
              }}
            >
              <Typography>Loading license details...</Typography>
            </Box>
          ) : (
            <TabContext value={selectedTab}>
              <Box
                sx={{
                  width: { xs: "800px", sm: "100%" },
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                <RootTabStyle
                  indicatorColor="transparent"
                  centered
                  length={tabsToShow.length}
                  onChange={handleTabChange}
                >
                  {tabsToShow.map((tab) => (
                    <RootTabItemStyles value={tab.value} label={tab.label} />
                  ))}
                </RootTabStyle>
              </Box>
              <TabPanel
                value="expiredLicenses"
                sx={{ width: { xs: "800px", sm: "100%" } }}
              >
              <BaseTable
                rows={chargersWithoutLicense || []}
                columns={licenseColumns(handleViewChargepoint)}
                getRowId={(row) => row.charge_box_pk}
                height={400}
                loading={licenseDetailsFetching}
              />
              </TabPanel>
              <TabPanel
                value="upcomingLicensesExpiry"
                sx={{ width: { xs: "800px", sm: "100%" } }}
              >
                <BaseTable
                rows={chargersWithUpcomingLicenseExpiry || []}
                columns={licenseColumns(handleViewChargepoint)}
                getRowId={(row) => row.charge_box_pk}
                height={400}
                loading={licenseDetailsFetching}
              />
              </TabPanel>
            </TabContext>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleLicenseModalClose} variant="outlined">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Page>
  );
}
